import type { IVector3 } from '@seddi/math-v2';
import { type PerspectiveCamera, Vector3 } from '@seddi/three';
import type { Dispatch } from 'redux';

import type { MockupLogo } from '../../types/mockupLogos';
import type { LogoRegion } from '../../viewer/viewerScene';
import { isRegionUpdateOnSelection } from '../../viewer/logosUpdates';
import { OrbitControls } from '../../viewer/orbitControls';
import { Raycaster } from '../../viewer/viewerRaycaster';
import { ViewerFrameControls } from '../../viewer/viewerFrameControls';
import { PickControls } from './pickControls';
import { TransformControls } from './transformControls';

const approxEquals = (a: Vector3, b: Vector3, epsilon = Number.EPSILON) => {
  return (
    Math.abs(a.x - b.x) < epsilon && Math.abs(a.y - b.y) < epsilon && Math.abs(a.z - b.z) < epsilon
  );
};

type ConstructorArgs = {
  camera: PerspectiveCamera;
  canvas: HTMLCanvasElement;
  dispatch: Dispatch;
  enableRaycast?: boolean;
  enableTransform?: boolean;
};

export class AppViewerTool {
  readonly camera: PerspectiveCamera;
  readonly canvas: HTMLCanvasElement;

  dispose: () => void;

  private readonly enableRaycast: boolean;
  private readonly enableTransform: boolean;
  private readonly raycaster: Raycaster | null;
  private readonly orbitControls: OrbitControls;
  private readonly frameControls: ViewerFrameControls;
  private readonly pickControls: PickControls | null;
  private readonly transformControls: TransformControls | null;
  private logos: MockupLogo[] = [];
  private selection: string | undefined;
  private isFrameDone = false;
  private readonly zoomDirection = new Vector3();
  private readonly cameraPositionPrev = new Vector3();
  private readonly targetPrev = new Vector3();

  constructor({
    camera,
    canvas,
    dispatch,
    enableRaycast = false,
    enableTransform = false,
  }: ConstructorArgs) {
    this.camera = camera;
    this.canvas = canvas;
    this.enableRaycast = enableRaycast;
    this.enableTransform = enableTransform;

    this.raycaster = enableRaycast ? new Raycaster() : null;

    const scope = this;
    this.frameControls = new ViewerFrameControls(camera, canvas);
    this.frameControls.enabled = false;
    this.frameControls.addEventListener('end', onFrameEnd);

    this.orbitControls = new OrbitControls(camera, canvas);
    this.orbitControls.dampingFactor = 0.1;
    this.orbitControls.enableDamping = true;

    if (enableRaycast) {
      this.pickControls = new PickControls(camera, canvas, this.raycaster!, dispatch);
      this.transformControls = new TransformControls(canvas, this.raycaster!, dispatch);
      this.setupPointerHandlers();
    } else {
      this.pickControls = null;
      this.transformControls = null;
    }

    this.lookAt({ x: 0.0, y: 1.2, z: 3.0 }, { x: 0.0, y: 1.2, z: 0.0 });

    this.dispose = () => {
      scope.frameControls.removeEventListener('end', onFrameEnd);
      if (enableRaycast) {
        canvas.removeEventListener('pointerdown', this.onPointerDownBound, { capture: true });
        canvas.removeEventListener('pointermove', this.onPointerMoveBound);
        canvas.removeEventListener('pointerup', this.onPointerUpBound, { capture: true });
        this.pickControls!.dispose();
        this.transformControls!.dispose();
        this.raycaster!.dispose();
      }
      this.orbitControls.dispose();
    };

    function onFrameEnd() {
      scope.orbitControls.target.copy(scope.frameControls.frame.sphere.center);
      scope.isFrameDone = true;
    }
  }

  private onPointerDownBound!: (event: PointerEvent) => void;
  private onPointerMoveBound!: (event: PointerEvent) => void;
  private onPointerUpBound!: () => void;

  private setupPointerHandlers() {
    const scope = this;
    const canvas = this.canvas;

    this.onPointerDownBound = function (event: PointerEvent) {
      if (scope.enableRaycast) {
        scope.pickControls!.onPointerDown(event);
        if (scope.enableTransform) {
          scope.transformControls!.onPointerDown(event);
          if (scope.transformControls!.isDown) {
            scope.orbitControls.enabled = false;
          }
        }
      }
    };

    this.onPointerMoveBound = function (event: PointerEvent) {
      if (scope.enableRaycast) {
        scope.pickControls!.onPointerMove(event);
        scope.transformControls!.onPointerMove(event);
      }
    };

    this.onPointerUpBound = function () {
      scope.orbitControls.enabled = true;
    };

    canvas.addEventListener('pointerdown', this.onPointerDownBound, { capture: true });
    canvas.addEventListener('pointermove', this.onPointerMoveBound, { capture: true });
    canvas.addEventListener('pointerup', this.onPointerUpBound, { capture: true });
  }

  lookAt(origin: IVector3, target: IVector3) {
    this.camera.position.copy(origin);
    this.camera.lookAt(target.x, target.y, target.z);
    this.orbitControls.target.copy(target);
  }

  setMaxDistance(distance: number) {
    this.orbitControls.maxDistance = distance;
  }

  zoomIn() {
    this.adjustZoom('in');
  }

  zoomOut() {
    this.adjustZoom('out');
  }

  private adjustZoom(direction: 'in' | 'out') {
    const target = this.orbitControls.target;

    this.zoomDirection.copy(this.camera.position).sub(target);

    const currentDistance = this.zoomDirection.length();
    if (currentDistance === 0) {
      return;
    }

    const zoomScale = Math.pow(0.95, this.orbitControls.zoomSpeed);
    const scaleFactor = direction === 'in' ? zoomScale : 1 / zoomScale;

    let newDistance = currentDistance * scaleFactor;
    newDistance = Math.max(
      this.orbitControls.minDistance,
      Math.min(this.orbitControls.maxDistance, newDistance),
    );

    this.zoomDirection.setLength(newDistance);
    this.camera.position.copy(target).add(this.zoomDirection);
  }

  onAnimationTick(): boolean {
    this.frameControls.update();
    if (this.frameControls.enabled && this.frameControls.target) {
      this.orbitControls.target.copy(this.frameControls.target);
    }
    this.orbitControls.update();

    if (this.enableRaycast) {
      this.pickControls!.update();
    }

    if (this.isFrameDone) {
      this.isFrameDone = false;
      this.frameControls.enabled = false;
      this.orbitControls.enabled = true;
      if (this.enableRaycast) {
        this.pickControls!.enabled = true;
        if (this.enableTransform) {
          this.transformControls!.enabled = true;
        }
      }
    }

    const needsDraw =
      !approxEquals(this.cameraPositionPrev, this.camera.position, 1e-4) ||
      !approxEquals(this.targetPrev, this.orbitControls.target, 1e-4);

    this.cameraPositionPrev.copy(this.camera.position);
    this.targetPrev.copy(this.orbitControls.target);

    return needsDraw;
  }

  onUpdate() {}

  updateGarmentGeometry(geometry: any) {
    this.frameControls.updateGarmentGeometry(geometry);
    if (this.enableRaycast && geometry) {
      this.raycaster!.buildGarmentGeometry(geometry);
    }
  }

  updateModel(model: any) {
    if (model) {
      model.updateWorldMatrix(false, true);
      this.frameControls.updateModel(model);
    }
    if (this.enableRaycast && model) {
      this.raycaster!.buildModel(model);
    }
  }

  updateLogos(logos: MockupLogo[]) {
    if (this.enableRaycast) {
      this.raycaster!.updateLogos(logos);
      this.transformControls!.logos = logos;
    }

    // Frame selection when logo region changes.
    const isFrameRequired = isRegionUpdateOnSelection(this.logos, logos, this.selection);
    if (isFrameRequired) {
      this.frameControls.updateSelection(logos, this.selection);
      this.frameControls.enabled = true;
      this.orbitControls.enabled = false;
      if (this.enableRaycast) {
        this.pickControls!.enabled = false;
        if (this.enableTransform) {
          this.transformControls!.enabled = false;
        }
      }
    }

    this.logos = logos;
  }

  updateRegions(regions: LogoRegion[]) {
    this.frameControls.updateRegions(regions);
    if (this.enableRaycast) {
      this.raycaster!.updateRegions(regions);
      this.pickControls!.enabled = true;
      if (this.enableTransform) {
        this.transformControls!.enabled = true;
      }
    }
  }

  updateSelection(selection: string | null) {
    this.selection = selection ?? undefined;
    if (this.enableRaycast) {
      this.pickControls!.selection = this.selection;
      this.transformControls!.selection = this.selection;
    }
    this.frameControls.updateSelection(this.logos, this.selection);

    // Only frame on selection, not on deselection.
    if (this.selection) {
      this.frameControls.enabled = true;
      this.orbitControls.enabled = false;
      if (this.enableRaycast) {
        this.pickControls!.enabled = false;
        if (this.enableTransform) {
          this.transformControls!.enabled = false;
        }
      }
    }
  }

  updateHover(hover: string | null) {
    if (this.enableRaycast) {
      this.pickControls!.hover = hover ?? undefined;
    }
  }
}
